
import React, { useEffect, useMemo, useRef } from "react";
import { Sidebar, SidebarContent, SidebarProvider, useSidebar } from "@/components/ui/sidebar";
import { useAuth } from "@/contexts/AuthContext";
import { useCommunityScope } from "@/contexts/CommunityScopeContext";
import { useNavigate, useLocation } from "react-router-dom";
import { SidebarHeader } from "./SidebarHeader";
import { SidebarFooter } from "./SidebarFooter";
import { SidebarNavigation } from "./SidebarNavigation";
import { MainContent } from "./MainContent";
import { BottomNavigation } from "./BottomNavigation";
import { OfflineStatusBanner } from "./OfflineStatusBanner";
import { OnboardingTourProvider } from "@/contexts/OnboardingTourContext";
import { OnboardingTour } from "@/components/onboarding/OnboardingTour";
import { CommunityScopeProvider } from "@/contexts/CommunityScopeContext";
import { ActiveCampaignModal } from "@/components/campaigns/ActiveCampaignModal";
import { normalizeCommunitySectionSelection } from "@/data/communitySections";

const SidebarRouteAutoCloser: React.FC = () => {
  const { isMobile, setOpenMobile } = useSidebar();
  const location = useLocation();
  const previousPathname = useRef(location.pathname);

  useEffect(() => {
    if (isMobile && previousPathname.current !== location.pathname) {
      setOpenMobile(false);
    }
    previousPathname.current = location.pathname;
  }, [location.pathname, isMobile, setOpenMobile]);

  return null;
};

const CommunityLandingRedirect: React.FC = () => {
  const { activeCommunity } = useCommunityScope();
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const lastRedirect = useRef<{ communityId?: string; route?: string }>({});

  const configuredSections = useMemo(
    () => normalizeCommunitySectionSelection(activeCommunity?.availableSections),
    [activeCommunity?.availableSections]
  );

  useEffect(() => {
    if (!activeCommunity || location.pathname !== '/app') {
      return;
    }
    if (currentUser?.role === 'admin') {
      return;
    }
    if (!configuredSections.length || configuredSections.includes('/app')) {
      return;
    }

    const preferredRoute = configuredSections.includes('/app/reservations')
      ? '/app/reservations'
      : configuredSections[0];

    const communityId = activeCommunity.id;
    if (
      preferredRoute &&
      preferredRoute !== location.pathname &&
      (lastRedirect.current.communityId !== communityId || lastRedirect.current.route !== preferredRoute)
    ) {
      lastRedirect.current = { communityId, route: preferredRoute };
      navigate(preferredRoute, { replace: true });
    }
  }, [activeCommunity, configuredSections, currentUser?.role, location.pathname, navigate]);

  return null;
};

const CommunitySectionGuard: React.FC = () => {
  const { activeCommunity } = useCommunityScope();
  const { currentUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const sectionGuardRedirect = useRef<{ communityId?: string; route?: string }>({});

  const allowedSections = useMemo(
    () => normalizeCommunitySectionSelection(activeCommunity?.availableSections),
    [activeCommunity?.availableSections]
  );

  useEffect(() => {
    if (!activeCommunity || currentUser?.role === 'admin') {
      return;
    }
    if (!allowedSections.length || location.pathname === '/app') {
      return;
    }

    const path = location.pathname;
    const isAlwaysAllowed =
      path === '/app/support' ||
      path.startsWith('/app/support/') ||
      path === '/app/profile' ||
      path.startsWith('/app/profile/');

    const isAllowed = isAlwaysAllowed || allowedSections.some(route => path === route || path.startsWith(`${route}/`));
    if (isAllowed) {
      return;
    }

    const fallbackRoute = allowedSections.includes('/app/reservations')
      ? '/app/reservations'
      : allowedSections[0];

    if (
      fallbackRoute &&
      fallbackRoute !== path &&
      (sectionGuardRedirect.current.communityId !== activeCommunity.id ||
        sectionGuardRedirect.current.route !== fallbackRoute)
    ) {
      sectionGuardRedirect.current = { communityId: activeCommunity.id, route: fallbackRoute };
      navigate(fallbackRoute, { replace: true });
    }
  }, [activeCommunity, allowedSections, currentUser?.role, location.pathname, navigate]);

  return null;
};

const OnboardingTourGate: React.FC = () => {
  const { activeCommunity, isGlobalAdmin } = useCommunityScope();
  const isCommunityReady = Boolean(activeCommunity);
  const isTourEnabled = isGlobalAdmin || isCommunityReady;
  const canRenderNavigation = isGlobalAdmin || isCommunityReady;

  return (
    <OnboardingTourProvider enabled={isTourEnabled}>
      <SidebarProvider>
        <OfflineStatusBanner />
        <div className="layout">
          <Sidebar>
            <SidebarHeader />
            <SidebarContent>
              {canRenderNavigation && <SidebarNavigation />}
            </SidebarContent>
            <SidebarFooter />
          </Sidebar>
          <MainContent />
          <BottomNavigation />
          <SidebarRouteAutoCloser />
          <CommunityLandingRedirect />
          <CommunitySectionGuard />
        </div>
      </SidebarProvider>
      {isTourEnabled && <OnboardingTour />}
      <ActiveCampaignModal />
    </OnboardingTourProvider>
  );
};

export function AppLayout() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Redirect to login if not authenticated
  useEffect(() => {
    if (!currentUser) {
      navigate("/login");
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    if (!currentUser || currentUser.role !== 'renter') {
      return;
    }

    const path = location.pathname;
    const allowedPrefixes = ['/app/reservations', '/app/calendar', '/app/announcements'];
    const isAllowedPath =
      allowedPrefixes.some(prefix => path === prefix || path.startsWith(`${prefix}/`)) || path === '/app/profile';

    if (!isAllowedPath) {
      navigate('/app/reservations', { replace: true });
    }
  }, [currentUser, location.pathname, navigate]);

  if (!currentUser) return null;
  
  return (
    <CommunityScopeProvider>
      <OnboardingTourGate />
    </CommunityScopeProvider>
  );
}
